import React, { useState, useEffect, useMemo } from 'react';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import UIButton from '@/components/shared/UIButton';
import UIInput from '@/components/shared/UIInput';
import FloatSelect from '@/components/shared/FloatSelect/FloatSelect';
import UIFormMessage from '@/components/shared/UIFormMessage'; // Import UIFormMessage
import { addUnitsToPropertyRequest } from '@/api';
import { useGetUnitOptions } from '@/hooks/useGetUnitOptions';
import './AddUnitFormSection.scss';

export interface AddUnitFormSectionProps {
  propertyId?: number | string;
  collapsed?: boolean;
  className?: string;
  onToggleCollapse?: (collapsed: boolean) => void;
  onUnitsAdded?: (addedUnits: any[]) => void;
  onClear?: () => void;
}

const baseClass = 'AddUnitFormSection';

interface Unit {
  number: string;
  type: string;
}

const createInitialUnits = (count: number, defaultType: string = ''): Unit[] => {
  return Array.from({ length: count }, () => ({ number: '', type: defaultType }));
};

const AddUnitFormSection: React.FC<AddUnitFormSectionProps> = ({
  propertyId,
  collapsed,
  className = '',
  onToggleCollapse,
  onUnitsAdded,
  onClear,
}) => {
  const { data: unitOptions, isLoading: isLoadingUnitOptions } = useGetUnitOptions();
  const [units, setUnits] = useState<Unit[]>(createInitialUnits(3));
  const [loading, setLoading] = useState(false); // State for Save button loading
  const [isClearing, setIsClearing] = useState(false); // State for Clear button loading
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const unitTypeOptions = useMemo(() => {
    const typeFilter = unitOptions?.filters.find((f: { key: string; }) => f.key === 'type');
    return typeFilter?.options?.filter((o: { value: string; }) => o.value) || []; // Filter out "All Types"
  }, [unitOptions]);

  useEffect(() => {
    if (unitTypeOptions.length > 0) {
      const defaultType = unitTypeOptions[0].value;
      setUnits(prevUnits =>
        prevUnits.map(unit => ({
          ...unit,
          type: unit.type || defaultType,
        }))
      );
    }
  }, [unitTypeOptions]);

  const handleUnitChange = (index: number, field: keyof Unit, value: string) => {
    const newUnits = [...units];
    newUnits[index][field] = value;
    setUnits(newUnits);
    if (error) setError(null);
    if (successMessage) setSuccessMessage(null); // Clear messages on input change
  };

  const handleAddUnit = () => {
    const defaultType = unitTypeOptions[0]?.value || '';
    setUnits([...units, { number: '', type: defaultType }]);
    if (error) setError(null);
    if (successMessage) setSuccessMessage(null); // Clear messages on adding unit
  };

  const handleRemoveUnit = (index: number) => {
    if (units.length > 1) {
      const newUnits = units.filter((_, i) => i !== index);
      setUnits(newUnits);
    }
    if (error) setError(null);
    if (successMessage) setSuccessMessage(null); // Clear messages on removing unit
  };

  const handleSave = async () => {
    if (!propertyId) {
      setError('Property ID is required');
      setSuccessMessage(null); // Ensure success message is cleared
      return;
    }

    const filledUnits = units
      .map(unit => ({
        number: unit.number.trim(),
        type: unit.type.trim(),
      }))
      .filter(unit => unit.number !== '');

    if (filledUnits.length === 0) {
      setError('Please enter at least one unit number');
      setSuccessMessage(null); // Ensure success message is cleared
      return;
    }

    if (filledUnits.some(unit => !unit.type)) {
      setError('Please select a type for each unit');
      setSuccessMessage(null); // Ensure success message is cleared
      return;
    }

    setLoading(true);
    setError(null); // Clear previous error
    setSuccessMessage(null); // Clear previous success message

    try {
      const numericPropertyId = typeof propertyId === 'string'
        ? parseInt(propertyId, 10)
        : propertyId;

      const response = await addUnitsToPropertyRequest(numericPropertyId, filledUnits);

      if (onUnitsAdded) {
        onUnitsAdded(response.data);
      }
      
      setSuccessMessage('Units added successfully!'); // Set success message
      // Do not call handleClear here directly, as it will clear messages immediately.
      // Instead, clear the form fields directly if needed, or let the user clear it.
      // For now, I'll keep the form filled after successful save, as per the original logic,
      // but clear messages. If the user wants to clear the form, they can click "Clear".

    } catch (error: any) {
      let messageToDisplay = 'Failed to add units. Please try again.';

      if (error.response && error.response.status === 400 && error.response.data?.errors) {
        const validationErrors = error.response.data.errors;
        const fieldErrors = Object.keys(validationErrors)
          .map(field => {
            const errors = validationErrors[field].join(', ');
            return `- ${field}: ${errors}`;
          })
          .join('\n');
        messageToDisplay = `${error.response.data.message || 'Validation failed'}:\n${fieldErrors}`;
      } else if (error.response?.data?.message) {
        messageToDisplay = error.response.data.message;
      } else if (error.message) {
        messageToDisplay = error.message;
      }
      setError(messageToDisplay);
      setSuccessMessage(null); // Ensure success message is cleared on error
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setIsClearing(true); // Start clearing
    setError(null); // Clear error message
    setSuccessMessage(null); // Clear success message

    // Simulate clearing process with a delay
    setTimeout(() => {
      const defaultType = unitTypeOptions[0]?.value || '';
      setUnits(createInitialUnits(3, defaultType));
      setIsClearing(false); // End clearing
      if (onClear) {
        onClear();
      }
    }, 500); // Show spinner for 0.5 seconds
  };

  // Function to clear messages when UIFormMessage closes
  const handleMessageClose = () => {
    setSuccessMessage(null);
    setError(null);
  };

  const renderContent = () => (
    <div className={`${baseClass}__content`}>
      <div className={`${baseClass}__form`}>
        {units.map((unit, index) => (
          <div key={index} className={`${baseClass}__unit-row`}>
            <UIInput
              value={unit.number}
              onChange={(e) => handleUnitChange(index, 'number', e.target.value)}
              placeholder="Unit Number"
              className={`${baseClass}__input-number`}
              disabled={loading || isClearing} // Disable inputs during save or clear
              label="Unit Number"
            />
            <div className={`${baseClass}__input-type-wrapper`}>
              <label className={`${baseClass}__label`}>Unit Type</label>
              <FloatSelect
                name={`unit-type-${index}`}
                options={unitTypeOptions}
                value={unit.type}
                onChange={(e) => handleUnitChange(index, 'type', e.target.value)}
                isDisabled={isLoadingUnitOptions || loading || isClearing} // Disable select during save or clear
                className={`${baseClass}__input-type`}
              />
            </div>
            <div className={`${baseClass}__button-wrapper`}>
              {index === units.length - 1 ? (
                <UIButton
                  variant="primary"
                  outline
                  size="medium"
                  onClick={handleAddUnit}
                  className={`${baseClass}__add-button`}
                  disabled={loading || isClearing} // Disable button during save or clear
                >
                  +
                </UIButton>
              ) : (
                <UIButton
                  variant="primary"
                  outline
                  size="medium"
                  onClick={() => handleRemoveUnit(index)}
                  className={`${baseClass}__remove-button`}
                  disabled={loading || isClearing || units.length <= 1} // Disable button during save or clear
                >
                  -
                </UIButton>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Display UIFormMessage for success or error */}
      {(successMessage || error) && (
        <UIFormMessage
          type={successMessage ? 'success' : 'error'}
          message={successMessage || error || ''}
          onClose={handleMessageClose}
        />
      )}

      <div className={`${baseClass}__actions`}>
        <UIButton
          variant="primary"
          size="medium"
          onClick={handleSave}
          loading={loading} // Use loading state for spinner
          disabled={loading || !propertyId || isLoadingUnitOptions || isClearing} // Disable if clearing is in progress
        >
          Save
        </UIButton>
        <UIButton
          variant="primary"
          outline
          size="medium"
          onClick={handleClear}
          loading={isClearing} // Use isClearing state for spinner
          disabled={loading || isClearing} // Disable if saving is in progress
        >
          Clear
        </UIButton>
      </div>
    </div>
  );

  return (
    <UICollapsibleSection
      title='Add Unit'
      description="" // Removed subtitle
      collapsible={true}
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      className={`${baseClass} ${className}`.trim()}
    >
      {renderContent()}
    </UICollapsibleSection>
  );
};

export default AddUnitFormSection;